import React, { useState, useEffect } from 'react';
import styles from '../Lists.module.css';
import { ListCommand } from '../../../types/ListCommands';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

export type RingBufferCommand = {
  id: number;
  action: 'enqueue';
  value: number;
} | {
  id: number;
  action: 'dequeue';
};
interface RingBufferViewProps {
  command: ListCommand | null; // FIX: Use the standard command type
  onComplete: () => void;
}

const BUFFER_SIZE = 8;

const RingBufferView: React.FC<RingBufferViewProps> = ({ command, onComplete }) => {
  const [buffer, setBuffer] = useState<(number | null)[]>(new Array(BUFFER_SIZE).fill(null));
  const [head, setHead] = useState(0);
  const [tail, setTail] = useState(0);
  const [size, setSize] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  useEffect(() => {
    if(command) executeCommand(command);
  }, [command]);
  
  const executeCommand = async (cmd: ListCommand) => {
    switch (cmd.action) {
      case 'addLast': {
        const isFull = size === BUFFER_SIZE;
        setHighlightedIndex(tail);
        await delay(500);

        const newBuffer = [...buffer];
        newBuffer[tail] = cmd.value;
        setBuffer(newBuffer);

        if (isFull) {
          setHead(h => (h + 1) % BUFFER_SIZE);
        } else {
          setSize(s => s + 1);
        }
        
        setTail(t => (t + 1) % BUFFER_SIZE);
        break;
      }
      case 'removeFirst': { 
        if (size > 0) {
            setHighlightedIndex(head);
            await delay(500);
            const newBuffer = [...buffer];
            newBuffer[head] = null;
            setBuffer(newBuffer);
            setHead(h => (h + 1) % BUFFER_SIZE);
            setSize(s => s - 1);
        }
        break;
      }
      case 'reset': {
        const newBuffer = new Array(BUFFER_SIZE).fill(null);
        setBuffer(newBuffer);
        setHead(0);
        setTail(0);
        setSize(0);
        await delay(300);
        break;
      }
    }
    await delay(500);
    setHighlightedIndex(null);
    onComplete();
  };

  const renderNodes = () => {
    const nodes = [];
    const radius = 80;
    const centerX = 80;
    const centerY = 80;

    for (let i = 0; i < BUFFER_SIZE; i++) {
      const angle = (i / BUFFER_SIZE) * 2 * Math.PI - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      const value = buffer[i];

      nodes.push(
        <div
          key={i}
          className={`${styles.rbNode} ${value === null ? styles.empty : ''} ${highlightedIndex === i ? styles.highlighted : ''}`}
          style={{ top: `${y}px`, left: `${x}px` }}
        >
          {value !== null ? value : i}
          {head === i && size > 0 && <div className={styles.rbPointer} style={{ top: '-20px', backgroundColor: '#2980b9' }}>H</div>}
          {tail === i && <div className={styles.rbPointer} style={{ bottom: '-20px', backgroundColor: '#c0392b' }}>T</div>}
        </div>
      );
    }
    return nodes;
  };

  return (
    <div className={styles.comparisonContainer}>
        <h3 className={styles.comparisonTitle}>Ring Buffer</h3>
        <div className={styles.visualization}>
            <div className={styles.ringBufferContainer}>{renderNodes()}</div>
        </div>
    </div>
  );
};

export default RingBufferView;